<template>
  <div class="client-selection-page">
    <div
      v-if="!hasAccess"
      class="access-denied-container d-flex align-items-center justify-content-center"
    >
      <div class="access-denied-content text-center px-2">
        <div class="access-denied-icon mb-2">
          <feather-icon icon="LockIcon" size="48" />
        </div>
        <h4 class="mb-1">Access Denied</h4>
        <p class="text-muted mb-1">
          You don't have permission to access the Client Dashboard.
        </p>
        <p class="text-muted small mb-2">
          Required: <code>read client_dashboard</code>, <code>manage client_dashboard</code>, or <code>manage all</code>
        </p>
        <b-button variant="primary" class="client-selection-btn" @click="$router.go(-1)">
          <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
          Go Back
        </b-button>
      </div>
    </div>

    <b-container v-else fluid>
      <b-row class="justify-content-center align-items-center client-selection-row">
        <b-col cols="12" sm="10" md="7" lg="5" xl="4">
          <b-card class="client-selection-card" no-body>
            <b-card-header class="client-selection-header py-2 px-3">
              <div class="client-selection-header__row">
                <div class="client-selection-icon">
                  <feather-icon icon="BriefcaseIcon" size="16" />
                </div>
                <div class="client-selection-header__body">
                  <span class="client-selection-header__title">Select Client Dashboard</span>
                  <span class="client-selection-header__hint">
                    · View client metrics, candidates, and reports
                  </span>
                </div>
              </div>
            </b-card-header>

            <b-card-body class="p-3 pt-2">
              <div v-if="loading" class="text-center py-2">
                <b-spinner variant="primary" label="Loading..." />
                <p class="mt-1 text-muted mb-0 small">Loading clients...</p>
              </div>

              <div v-else-if="error" class="text-center py-2">
                <feather-icon icon="AlertCircleIcon" size="32" class="text-danger mb-1" />
                <h6 class="text-danger mb-1">Error Loading Clients</h6>
                <p class="text-muted small mb-2">{{ error }}</p>
                <b-button
                  variant="primary"
                  size="sm"
                  class="client-selection-btn"
                  @click="loadClients"
                >
                  <feather-icon icon="RefreshCwIcon" size="14" class="mr-1" />
                  Retry
                </b-button>
              </div>

              <div v-else-if="clients.length === 0" class="text-center py-2">
                <feather-icon icon="InfoIcon" size="32" class="text-muted mb-1" />
                <h6 class="mb-1">No Clients Available</h6>
                <p class="text-muted mb-0 small">There are no clients available at the moment.</p>
              </div>

              <b-form v-else @submit.prevent="handleSubmit">
                <b-form-group
                  label="Client"
                  label-for="client-select"
                  class="mb-1"
                >
                  <b-form-select
                    id="client-select"
                    v-model="selectedClient"
                    :options="clientOptions"
                    required
                    :state="validationState"
                  >
                    <template #first>
                      <option :value="null" disabled>-- Select a client --</option>
                    </template>
                  </b-form-select>
                  <b-form-invalid-feedback v-if="!selectedClient && submitted">
                    Please select a client to continue
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-button
                  type="submit"
                  variant="primary"
                  block
                  class="client-selection-btn mt-1"
                  :disabled="!selectedClient || submitting"
                >
                  <feather-icon icon="ArrowRightIcon" size="16" class="mr-1" />
                  {{ submitting ? 'Loading...' : 'View Dashboard' }}
                </b-button>
              </b-form>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import {
  BContainer,
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BForm,
  BFormGroup,
  BFormSelect,
  BFormInvalidFeedback,
  BButton,
  BSpinner
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import clientService from '@/services/client-service'
import ability from '@/libs/acl/ability'
import {
  canAccessClientDashboard,
} from '@/utils/client-dashboard-permissions'

export default {
  name: 'ClientSelection',
  components: {
    BContainer,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormInvalidFeedback,
    BButton,
    BSpinner,
    FeatherIcon
  },
  data() {
    return {
      loading: true,
      error: null,
      clients: [],
      selectedClient: null,
      submitted: false,
      submitting: false
    }
  },
  computed: {
    clientOptions() {
      return this.clients.map(client => ({
        value: client.client_name || client.name || client.title,
        text: client.client_name || client.name || client.title
      }))
    },
    validationState() {
      if (!this.submitted) return null
      return !!this.selectedClient
    },
    hasAccess() {
      return canAccessClientDashboard(ability.can.bind(ability))
    }
  },
  mounted() {
    if (!this.hasAccess) {
      return
    }
    this.loadClients()
  },
  methods: {
    async loadClients() {
      this.loading = true
      this.error = null
      try {
        const response = await clientService.getClients()
        if (response && response.status === 'OK' && response.data) {
          this.clients = Array.isArray(response.data) ? response.data : []
        } else {
          this.clients = []
        }
      } catch (err) {
        console.error('Error loading clients:', err)
        this.error = err.message || 'Failed to load clients. Please try again.'
        this.clients = []
      } finally {
        this.loading = false
      }
    },
    handleSubmit() {
      this.submitted = true
      if (!this.selectedClient) {
        return
      }

      this.submitting = true
      const encodedClientName = encodeURIComponent(this.selectedClient)
      this.$router.push({
        name: 'dashboard-client',
        params: { clientName: encodedClientName }
      })
    }
  }
}
</script>

<style scoped>
.client-selection-page {
  --cs-primary: #00ac99;
  min-height: calc(100vh - 4rem);
  padding: 1rem 0;
  background:
    radial-gradient(ellipse at top left, rgba(0, 172, 153, 0.1), transparent 45%),
    radial-gradient(ellipse at bottom right, rgba(40, 199, 111, 0.07), transparent 40%),
    #f8f8f8;
}

.client-selection-row {
  min-height: 50vh;
}

.client-selection-card {
  border: 1px solid #e3e6f0;
  border-radius: 10px;
  box-shadow: 0 3px 14px rgba(34, 41, 47, 0.07);
  overflow: hidden;
}

.client-selection-header {
  background: #fff;
  border-bottom: 1px solid #e3e6f0;
}

.client-selection-header__row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}

.client-selection-header__body {
  min-width: 0;
  flex: 1 1 auto;
  line-height: 1.35;
}

.client-selection-header__title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #5e5873;
  letter-spacing: -0.01em;
}

.client-selection-header__hint {
  font-size: 0.8125rem;
  color: #6e6b7b;
}

.client-selection-icon {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(0, 172, 153, 0.12);
  color: var(--cs-primary);
}

.client-selection-card >>> .form-group {
  margin-bottom: 0.35rem;
}

.client-selection-card >>> .form-group label {
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
}

.client-selection-btn {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

#client-select {
  font-size: 0.95rem;
  min-height: 40px;
}

@media (max-width: 575.98px) {
  .client-selection-page {
    padding: 0.5rem 0;
  }

  .client-selection-row {
    min-height: auto;
    padding-top: 0.75rem;
  }

  .client-selection-btn,
  #client-select {
    min-height: 44px;
  }
}

.access-denied-container {
  min-height: calc(100vh - 4rem);
}

.access-denied-content {
  max-width: 380px;
}

.access-denied-icon {
  color: #6c757d;
}
</style>
